import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StatusPill } from "@/components/StatusPill";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { plural } from "@/lib/plural";
import type { MemberResource, ResourceRoute, RouteState } from "./shared";

export function ResourceStatusPill({ state }: { state: RouteState }) {
  if (state === "NoRoute") {
    return <StatusPill variant="warning">No route</StatusPill>;
  }

  if (state === "Reachable") {
    return <StatusPill variant="online">Reachable</StatusPill>;
  }

  if (state === "Unreachable") {
    return <StatusPill variant="offline">Unreachable</StatusPill>;
  }

  return <StatusPill variant="idle">Not yet checked</StatusPill>;
}

const ROUTE_BAR_CLASSES: Record<string, string> = {
  Reachable: "bg-success",
  Unreachable: "bg-danger",
};

const ROUTE_STATE_LABELS: Record<string, string> = {
  Reachable: "Reachable",
  Unreachable: "Unreachable",
  NotChecked: "Not yet checked",
  Unknown: "Unknown",
};

function routeName(route: ResourceRoute) {
  return [route.gatewayName, route.connectorName]
    .filter((name) => name !== null)
    .join(" → ");
}

function routeDetail(route: ResourceRoute) {
  const offline =
    route.state === "Unknown"
      ? `The ${route.connectorName !== null ? "connector" : "gateway"} is offline.`
      : null;
  const checked = route.lastCheckedAt
    ? `Last checked ${formatRelativeTime(route.lastCheckedAt)}.`
    : null;

  return [offline, checked].filter((part) => part !== null).join(" ");
}

const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 8;

export function ResourceRouteCell({ resource }: { resource: MemberResource }) {
  if (resource.routeState === "NoRoute") {
    return <span className="text-sm text-foreground/70">Nothing assigned</span>;
  }

  return <RouteCount routes={resource.routes} />;
}

// The tooltip is portalled because the table's scroll container clips it.
function RouteCount({ routes }: { routes: ResourceRoute[] }) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!open || !trigger || !tooltip) {
      setPosition(null);
      return;
    }

    const anchor = trigger.getBoundingClientRect();
    const { offsetHeight: height, offsetWidth: width } = tooltip;
    const opensUp =
      anchor.bottom + TOOLTIP_GAP + height > window.innerHeight &&
      anchor.top - TOOLTIP_GAP - height > 0;

    setPosition({
      top: opensUp
        ? anchor.top - TOOLTIP_GAP - height
        : anchor.bottom + TOOLTIP_GAP,
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchor.left, window.innerWidth - width - VIEWPORT_MARGIN),
      ),
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function close() {
      setOpen(false);
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
      }
    }

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const reachable = routes.filter(
    (route) => route.state === "Reachable",
  ).length;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        <span className="flex gap-0.5" aria-hidden="true">
          {routes.map((route, index) => (
            <span
              key={index}
              className={`h-3 w-1 rounded-sm ${ROUTE_BAR_CLASSES[route.state] ?? "bg-muted"}`}
            />
          ))}
        </span>
        <span className="text-sm tabular-nums text-foreground/70 underline decoration-muted decoration-dotted underline-offset-4">
          {reachable} of {plural(routes.length, "route")} reachable
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            id={tooltipId}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
            }}
            className="pointer-events-none fixed z-50 w-max max-w-sm rounded-lg border border-muted bg-surface p-3 text-sm shadow-lg"
          >
            <div className="flex flex-col gap-2">
              {routes.map((route, index) => (
                <div key={index} className="flex flex-col gap-0.5">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-foreground">{routeName(route)}</span>
                    <span className="shrink-0 text-xs text-foreground/70">
                      {ROUTE_STATE_LABELS[route.state] ?? route.state}
                    </span>
                  </div>
                  {routeDetail(route) && (
                    <span className="text-xs text-foreground/70">
                      {routeDetail(route)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
